import React from 'react';
import { motion } from 'framer-motion';
import { animationPresets } from '../../utils/animationPresets';
import '../../styles/glassmorphism.css';

/**
 * UNIFIED SAS CARD COMPONENT
 * 
 * Master card component for all sections (Admin & Public)
 * Handles:
 * - Multiple glass variants (light, dark, minimal)
 * - Consistent animations via animationPresets
 * - Responsive sizing
 * - Accessibility (focus states, motion preferences)
 * 
 * Usage:
 * <SASCard variant="light" hoverable delay={0.1}>
 *   <Card.Header>Title</Card.Header>
 *   <Card.Body>Content</Card.Body>
 *   <Card.Footer>Actions</Card.Footer>
 * </SASCard>
 * 
 * @prop {string} variant - 'light' | 'dark' | 'minimal' (default: 'light')
 * @prop {string} size - 'sm' | 'md' | 'lg' (default: 'md')
 * @prop {boolean} hoverable - Enable hover lift effect (default: true)
 * @prop {boolean} interactive - Enable tap scale (default: false)
 * @prop {number} delay - Animation delay in seconds (default: 0)
 * @prop {string} className - Additional CSS classes
 * @prop {React.ReactNode} children - Card content
 */
const SASCard = React.forwardRef(({
  variant = 'light',
  size = 'md',
  hoverable = true,
  interactive = false,
  delay = 0,
  className = '',
  children,
  ...props
}, ref) => {
  const sizeMap = {
    sm: { padding: '12px 16px', minHeight: 'auto' },
    md: { padding: '20px 24px', minHeight: 'auto' },
    lg: { padding: '28px 32px', minHeight: 'auto' }
  };

  const variantClasses = {
    light: 'glass-light glass-card variant-light',
    dark: 'glass-dark glass-card variant-dark',
    minimal: 'glass-minimal glass-card variant-minimal'
  };

  const motionProps = {
    ...animationPresets.slideUpOnView,
    transition: {
      ...animationPresets.slideUpOnView.transition,
      delay,
      type: 'spring',
      stiffness: 280,
      damping: 18
    }
  };

  const hoverProps = hoverable ? { 
    whileHover: { y: -12, scale: 1.02 }, 
    transition: { type: 'spring', stiffness: 300, damping: 20 } 
  } : {};
  const tapProps = interactive ? animationPresets.tapScale : {};

  return (
    <motion.div
      ref={ref}
      className={`${variantClasses[variant] || variantClasses.light} ${className}`.trim()}
      style={sizeMap[size]}
      {...motionProps}
      {...hoverProps}
      {...tapProps}
      {...props}
    >
      {children}
    </motion.div>
  );
});

SASCard.displayName = 'SASCard';

/**
 * Card Header - For titles, icons, badges
 * Usage: <Card.Header>My Title</Card.Header>
 */
const CardHeader = ({ children, className = '', ...props }) => (
  <div
    className={`card-header ${className}`.trim()}
    style={{
      marginBottom: '12px',
      paddingBottom: '12px',
      borderBottom: 'rgba(0, 0, 0, 0.05)'
    }}
    {...props}
  >
    {children}
  </div>
);

/**
 * Card Body - Main content area
 * Usage: <Card.Body>Content here</Card.Body>
 */
const CardBody = ({ children, className = '', ...props }) => (
  <div className={`card-body ${className}`.trim()} {...props}>
    {children}
  </div>
);

/**
 * Card Footer - Actions, buttons, metadata
 * Usage: <Card.Footer>Footer content</Card.Footer>
 */
const CardFooter = ({ children, className = '', ...props }) => (
  <div
    className={`card-footer ${className}`.trim()}
    style={{
      marginTop: '16px',
      paddingTop: '12px',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      display: 'flex',
      gap: '8px',
      justifyContent: 'flex-end',
      flexWrap: 'wrap'
    }}
    {...props}
  >
    {children}
  </div>
);

// Attach sub-components
SASCard.Header = CardHeader;
SASCard.Body = CardBody;
SASCard.Footer = CardFooter;

export default SASCard;

/**
 * QUICK VARIANTS (Pre-configured for common use cases)
 */

// Stat Card - Dashboard statistics
export const StatCard = ({
  icon,
  label,
  value,
  badge,
  color = 'var(--sindhi-maroon)',
  ...props
}) => (
  <SASCard variant="light" hoverable delay={props.delay || 0} {...props}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--sindhi-text-muted)',
            marginBottom: '8px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: '32px',
            fontWeight: 800,
            fontFamily: 'var(--font-display)',
            color: 'var(--sindhi-navy)',
            letterSpacing: '-1px'
          }}
        >
          {value}
        </div>
        {badge && (
          <div
            style={{
              fontSize: '11px',
              color: color,
              marginTop: '8px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              background: 'rgba(0, 0, 0, 0.04)',
              padding: '4px 8px',
              borderRadius: '6px',
              width: 'fit-content'
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                background: color,
                opacity: 0.7
              }}
            />
            {badge}
          </div>
        )}
      </div>
      {icon && (
        <div
          style={{
            fontSize: '28px',
            opacity: 0.3,
            lineHeight: 1
          }}
        >
          {icon}
        </div>
      )}
    </div>
  </SASCard>
);

// Modal Card - For overlays and dialogs
export const ModalCard = ({ children, ...props }) => (
  <SASCard variant="dark" hoverable={false} {...props}>
    {children}
  </SASCard>
);

// Manuscript/Content Card - For books, articles
export const ContentCard = ({
  image,
  title,
  description,
  metadata,
  action,
  ...props
}) => (
  <SASCard variant="light" interactive {...props}>
    {image && (
      <div
        style={{
          width: '100%',
          height: '200px',
          marginBottom: '12px',
          borderRadius: '12px',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, rgba(0,0,0,0.05), rgba(0,0,0,0.02))'
        }}
      >
        <img
          src={image}
          alt={title}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      </div>
    )}
    {title && (
      <h3
        style={{
          margin: '0 0 8px 0',
          fontSize: '16px',
          fontWeight: 700,
          color: 'var(--sindhi-navy)'
        }}
      >
        {title}
      </h3>
    )}
    {description && (
      <p
        style={{
          margin: '0 0 12px 0',
          fontSize: '14px',
          color: 'var(--sindhi-text-muted)',
          lineHeight: '1.5'
        }}
      >
        {description}
      </p>
    )}
    {metadata && (
      <div
        style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}
      >
        {metadata.map((tag, i) => (
          <span
            key={i}
            className="glass-badge"
            style={{
              background: 'rgba(0, 0, 0, 0.06)',
              color: 'var(--sindhi-text-muted)',
              borderColor: 'rgba(0, 0, 0, 0.1)'
            }}
          >
            {tag}
          </span>
        ))}
      </div>
    )}
    {action && (
      <div
        style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: '1px solid rgba(0, 0, 0, 0.05)'
        }}
      >
        {action}
      </div>
    )}
  </SASCard>
);
